// src/components/atomic-crm/companies/CompanyRow.tsx
import { Link as RouterLink } from "react-router-dom";
import { useRecordContext, useTranslate, useLocale } from "ra-core";
import { Building2, Users, DollarSign, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { getDateFnsLocale } from "@/i18n/date-fns";
import { CompanyAvatar } from "./CompanyAvatar";
import type { Company } from "../types";

export const CompanyRow = () => {
  const company = useRecordContext<Company>();
  const translate = useTranslate();
  const locale = useLocale();

  if (!company) return null;

  const nbContacts = company.nb_contacts ?? 0;
  const nbDeals = company.nb_deals ?? 0;

  return (
    <RouterLink
      to={`/companies/${company.id}/show`}
      className="flex items-start gap-3 px-3 py-3 hover:bg-muted/50 transition-colors"
    >
      <div className="shrink-0">
        <CompanyAvatar />
      </div>

      <div className="flex-1 min-w-0">
        {/* Line 1: name */}
        <div className="font-medium truncate">{company.name}</div>

        {/* Line 2: sector */}
        {company.sector && (
          <div className="text-xs text-muted-foreground truncate">
            {company.sector}
          </div>
        )}

        {/* Line 3: counts + last seen */}
        <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
          {nbContacts > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {nbContacts}
            </span>
          )}
          {nbDeals > 0 && (
            <span className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              {nbDeals}
            </span>
          )}
          {company.last_seen && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(company.last_seen), {
                addSuffix: true,
                locale: getDateFnsLocale(locale),
              })}
            </span>
          )}
        </div>
      </div>
    </RouterLink>
  );
};
